import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { ContentHeadingData } from 'common/models/content-heading';
import { WhatsNewResultsHelperService, WhatsNewResultRow as WhatsNewResultTableRow } from 'private/app/services/whats-new-results-helper.service';
import { Option } from 'common/components/select/select.component';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DocumentService } from 'common/services/document.service';
import { delay, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { ContentState } from 'common/store/content/content.state';
import { BrandList } from 'common/models/brand.model';
import { GMTTimeConversionUtilsService } from 'common/services/gmt-time-transformation.service';
import { endOfYear } from 'date-fns';
import { DateRange } from 'private/app/models/account-admin-program.model';
import { DocumentResult, WhatsNewResultRow as WhatsNewResultQuery } from 'common/models/document';
import { AppConstants } from 'common/app-constants';
import { ToastService } from 'common/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'hvac-whats-new-results',
    templateUrl: './whats-new-results.component.html',
    styleUrls: ['./whats-new-results.component.scss']
})
export class WhatsNewResultsComponent implements OnInit {
    @Select(ContentState.contentBrandList) availableBrandsList$!: Observable<BrandList[]>;

    public whatsNewResultsForm = WhatsNewResultsHelperService.WhatsNewResultsForm;
    public dateOptions: Option[];
    public documentTypeOptions: Option[];
    public currentPage: number = 1;
    public pageSize: number = 10;
    public totalPages: number;
    public maxSearchResultsLimit: number = 10000;
    public loading: boolean = false;
    public showDateRangePicker: boolean = true;
    public selectedStartDate: number | undefined;
    public selectedEndDate: number | undefined;
    public heading: ContentHeadingData = { Content: { title: 'What\'s New Results' } }
    public ngOnDestroy$ = new Subject();
    public searchCancel$ = new Subject<void>();
    public todayDate: number = this.gmtTimeConversionUtilsService.getCurrentGMTTime({ formatTimeAs: 'StartOfDay' })
    public endOfYearDate: number = endOfYear(this.gmtTimeConversionUtilsService.getCurrentGMTTime({ formatTimeAs: 'EndOfDay' })).setHours(23, 59, 59, 999);

    public brandsList$: Observable<Option[]>;
    public query: WhatsNewResultQuery;
    public allSearchResults: WhatsNewResultTableRow[] = [];
    public filteredSearchResults: WhatsNewResultTableRow[] = [];
    public searchResults$: BehaviorSubject<WhatsNewResultTableRow[]> = new BehaviorSubject<WhatsNewResultTableRow[]>([]);
    public resultsFilterForm = new FormGroup({
        title: new FormControl(''),
        catalogNumber: new FormControl(''),
        documentType: new FormControl('')
    });

    constructor(
        public whatsNewResultsHelper: WhatsNewResultsHelperService,
        private gmtTimeConversionUtilsService: GMTTimeConversionUtilsService,
        private translate: TranslateService,
        private readonly documentService: DocumentService,
        private readonly toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.dateOptions = this.whatsNewResultsHelper.getDateOptions();
        this.documentTypeOptions = this.whatsNewResultsHelper.getDropDownOptions();
        this.setDefaultDateSelection();
        this.subscribeToResultsFilterChanges();

        this.brandsList$ = this.availableBrandsList$.pipe(map((brandList) => {
            const optionList: Option[] = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            brandList.forEach((availBrand: any) => {
                if (availBrand.brandName.toLowerCase() !== 'default') {
                    optionList.push(
                        {
                            name: availBrand.brandName,
                            value: availBrand.brandName
                        }
                    );
                }
            });
            optionList.sort((a, b) => a.name.localeCompare(b.name));

            return optionList;
        }));
    }

    public setDefaultDateSelection() {
        const selectDateRangeOption = this.dateOptions.find(
            (option) => option.value === WhatsNewResultsHelperService.SELECT_DATE_RANGE_VALUE
        );

        if (selectDateRangeOption) {
            this.whatsNewResultsForm.controls.date.setValue([selectDateRangeOption]);
            this.showDateRangePicker = true;
            this.applyDateRange(this.getLastWeekDateRange());
        }
    }

    ngOnDestroy() {
        this.searchCancel$.next();
        this.searchCancel$.complete();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    onDateChange(event: Option[]) {
        const selectedDateValue = event?.[0]?.value;
        const isCustomDateRange = selectedDateValue === WhatsNewResultsHelperService.SELECT_DATE_RANGE_VALUE;
        this.showDateRangePicker = true;

        if (isCustomDateRange) {
            if (!this.selectedStartDate || !this.selectedEndDate) {
                this.applyDateRange(this.getLastWeekDateRange());
            }

            return;
        }

        const presetRange = this.whatsNewResultsHelper.getPresetDateRange(
            selectedDateValue,
            this.gmtTimeConversionUtilsService.getCurrentGMTTime({ formatTimeAs: 'EndOfDay' })
        );

        if (presetRange?.startDate && presetRange?.endDate) {
            this.applyDateRange(presetRange);
        }
    }

    setFromToDate(dateRangeDates: DateRange) {
        this.applyDateRange(dateRangeDates);
    }

    getLastWeekDateRange(): DateRange {
        return this.whatsNewResultsHelper.getPresetDateRange(
            'LAST_WEEK',
            this.gmtTimeConversionUtilsService.getCurrentGMTTime({ formatTimeAs: 'EndOfDay' })
        ) as DateRange;
    }

    applyDateRange(dateRangeDates: DateRange) {
        this.selectedStartDate = dateRangeDates.startDate;
        this.selectedEndDate = dateRangeDates.endDate;
        this.whatsNewResultsForm.controls.fromDate.setValue(dateRangeDates.startDate ?? '');
        this.whatsNewResultsForm.controls.toDate.setValue(dateRangeDates.endDate ?? '');
        this.whatsNewResultsForm.controls.fromDate.setValidators([Validators.required]);
        this.whatsNewResultsForm.controls.toDate.setValidators([Validators.required]);
        this.whatsNewResultsForm.controls.fromDate.updateValueAndValidity();
        this.whatsNewResultsForm.controls.toDate.updateValueAndValidity();
    }

    clearAll() {
        this.searchCancel$.next();
        this.loading = false;
        this.whatsNewResultsForm.reset({
            brand: '',
            documentType: ''
        });
        this.setDefaultDateSelection();
        this.resultsFilterForm.reset({
            title: '',
            catalogNumber: '',
            documentType: ''
        }, { emitEvent: false });
        this.allSearchResults = [];
        this.filteredSearchResults = [];
        this.searchResults$.next([]);
        this.currentPage = 1;
        this.totalPages = 0;
        this.toastService.remove('whatsNewResultsMaxSearchResultsLimit');
    }

    searchNewResults(event: UntypedFormGroup) {
        this.searchCancel$.next();
        this.loading = true;
        this.currentPage = 1;
        this.resultsFilterForm.reset({
            title: '',
            catalogNumber: '',
            documentType: ''
        }, { emitEvent: false });
        this.query = this.buildDocumentQuery(event);

        this.documentService.getWhatsNewResultsDocuments(this.query).pipe(
            takeUntil(this.searchCancel$),
            takeUntil(this.ngOnDestroy$)
        ).subscribe((results: DocumentResult) => {
            this.loadDocumentResults(results);
            this.loading = false;
        }, (_error) => {
            this.allSearchResults = [];
            this.filteredSearchResults = [];
            this.searchResults$.next([]);
            this.totalPages = 0;
            this.loading = false;
        });
    }

    buildDocumentQuery(form: UntypedFormGroup): WhatsNewResultQuery {
        const formValue = form.value;

        return {
            Resultcount: this.maxSearchResultsLimit.toString(),
            Brands: this.getSelectedOptionValues(formValue.brand),
            PublishedDate: this.whatsNewResultsHelper.formatDateForPayload(formValue.fromDate),
            ReleaseToDate: this.whatsNewResultsHelper.formatDateForPayload(formValue.toDate),
            SubType: this.getSelectedOptionValues(formValue.documentType)
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getSelectedOptionValues(selectedOptions: any): string[] {
        if (!Array.isArray(selectedOptions)) {
            return [];
        }

        return selectedOptions.map((option: Option) => option?.value).filter((value: string) => Boolean(value));
    }

    loadDocumentResults(results: DocumentResult) {
        const documents = results?.Documents || [];
        this.allSearchResults = this.whatsNewResultsHelper.mapDocumentsToResults(documents);
        if (this.allSearchResults.length === this.maxSearchResultsLimit) {
            this.toastService.add({
                content: this.translate.instant('WHATS_NEW_RESULTS.MAX_SEARCH_RESULTS_LIMIT'),
                theme: 'warning',
                id: 'whatsNewResultsMaxSearchResultsLimit',
                outletName: 'whatsNewResultsMaxSearchResultsLimit',
                closeable: false,
                autoClose: false
            });
        }
        this.filterSearchResults();
    }

    filterSearchResults() {
        const titleFilter = (this.resultsFilterForm.controls.title.value || '').toLowerCase().trim();
        const catalogNumberFilter = (this.resultsFilterForm.controls.catalogNumber.value || '').toLowerCase().trim();
        const documentTypeFilter = (this.resultsFilterForm.controls.documentType.value || '').toLowerCase().trim();

        this.filteredSearchResults = this.allSearchResults.filter((result) => {
            const matchesTitle = !titleFilter || result.title?.toLowerCase().includes(titleFilter);
            const matchesCatalogNumber = !catalogNumberFilter ||
                result.catalogNumber?.toLowerCase().includes(catalogNumberFilter);
            const matchesDocumentType = !documentTypeFilter ||
                result.type?.toLowerCase().includes(documentTypeFilter);

            return matchesTitle && matchesCatalogNumber && matchesDocumentType;
        });

        this.currentPage = 1;
        this.totalPages = Math.ceil(this.filteredSearchResults.length / this.pageSize) || 0;
        this.setPagedResults();
    }

    handlePageChange(page: number) {
        if (page === this.currentPage) {
            return;
        }

        this.currentPage = page;
        this.setPagedResults();
    }

    setPagedResults() {
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.searchResults$.next(this.filteredSearchResults.slice(startIndex, endIndex));
    }

    onResultRowClick(result: WhatsNewResultTableRow) {
        if (!result?.documentUrl) {
            return;
        }

        window.open(result.documentUrl, '_blank', 'noopener,noreferrer');
    }

    private subscribeToResultsFilterChanges() {
        this.resultsFilterForm.valueChanges.pipe(
            takeUntil(this.ngOnDestroy$),
            distinctUntilChanged(),
            delay(AppConstants.enrollmentsSearchDelay)
        ).subscribe(() => {
            this.filterSearchResults();
        });
    }
}
